import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useState, useMemo, useEffect } from "react";
import { ArrowRight, ArrowUpRight, Search, MoveUpRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteShell } from "@/components/SiteShell";
import { fetchArticles, optimizeImageUrl, type Article } from "@/lib/articles";
import { formatDisplayDate, formatReadTime } from "@/lib/utils";
import { fetchAuthorProfile, getAuthorSocial } from "@/lib/author";

const HeroScene = lazy(() => import("@/components/HeroScene"));

export const Route = createFileRoute("/")({
  loader: async () => {
    const [articles, profile] = await Promise.all([fetchArticles(), fetchAuthorProfile()]);
    return { articles, profile };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "BuildWithJIT | AI Automation & Systems Engineering" },
      {
        name: "description",
        content: `Practical guides and architectural deep-dives on AI automation, RAG pipelines, voice AI agents, and enterprise engineering by ${loaderData?.profile.name ?? "our authors"}.`,
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "googlebot",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "BuildWithJIT" },
      { property: "og:title", content: "BuildWithJIT | AI Automation & Systems Engineering" },
      {
        property: "og:description",
        content: `Practical guides and architectural deep-dives on AI automation, RAG pipelines, voice AI agents, and enterprise engineering by ${loaderData?.profile.name ?? "our authors"}.`,
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://buildwithjit.com/" },
      { property: "og:image", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:secure_url", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "BuildWithJIT: Production AI Automation & Systems Engineering",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@buildwithjit" },
      { name: "twitter:creator", content: "@buildwithjit" },
      { name: "twitter:title", content: "BuildWithJIT | AI Automation & Systems Engineering" },
      {
        name: "twitter:description",
        content: `Practical guides and architectural deep-dives on AI automation, RAG pipelines, voice AI agents, and enterprise engineering by ${loaderData?.profile.name ?? "our authors"}.`,
      },
      { name: "twitter:image", content: "https://buildwithjit.com/og-share.png" },
      { name: "yandex-verification", content: "05bf959c4052afda" },
      { name: "Petal-Search-site-verification", content: "2bd17b1890" },
      { name: "naver-site-verification", content: "46d8b90dca652bea0286958cc9ceccc16a4a537b" },
    ],
    links: [
      { rel: "canonical", href: "https://buildwithjit.com/" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  component: Home,
});

function Home() {
  const { articles: initialArticles, profile } = Route.useLoaderData();
  const social = getAuthorSocial(profile);
  const [articles, setArticles] = useState<Article[]>(initialArticles || []);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [showHero3D, setShowHero3D] = useState(false);

  // Defer heavy 3D WebGL Canvas loading until the main thread finishes initial paint
  useEffect(() => {
    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        const id = (
          window as unknown as {
            requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number;
          }
        ).requestIdleCallback(() => setShowHero3D(true), { timeout: 1200 });
        return () =>
          (window as unknown as { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(
            id,
          );
      } else {
        const timer = setTimeout(() => setShowHero3D(true), 300);
        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, []);

  // Client-side hydration sync to guarantee latest database state
  useEffect(() => {
    let active = true;
    fetchArticles()
      .then((data) => {
        if (active && data && data.length > 0) {
          setArticles(data);
        }
      })
      .catch((err) => console.warn("Could not sync homepage articles in client:", err));

    return () => {
      active = false;
    };
  }, []);

  // Sort articles newest-first based on published date
  const sortedArticles = useMemo(() => {
    return [...articles].sort((a: Article, b: Article) => {
      const timeA = new Date(a.date).getTime() || 0;
      const timeB = new Date(b.date).getTime() || 0;
      return timeB - timeA;
    });
  }, [articles]);

  // Extract unique categories dynamically from database articles
  const categories = useMemo(() => {
    const set = new Set<string>();
    sortedArticles.forEach((a: Article) => {
      const cat = (a.category || "").trim();
      if (cat) set.add(cat);
    });
    return ["All", ...Array.from(set)];
  }, [sortedArticles]);

  const filtered = useMemo(() => {
    return sortedArticles.filter((a: Article) => {
      const matchesCategory =
        category === "All" ||
        (a.category && a.category.trim().toLowerCase() === category.trim().toLowerCase());
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        (a.category && a.category.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [sortedArticles, category, query]);

  const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": "https://buildwithjit.com/#navigation",
    name: "Primary Navigation",
    itemListElement: [
      {
        "@type": "SiteNavigationElement",
        position: 1,
        name: "Articles & Technical Deep-Dives",
        description:
          "Production guides on AI automation, RAG pipelines, voice AI agents, and systems engineering.",
        url: "https://buildwithjit.com/articles",
      },
      {
        "@type": "SiteNavigationElement",
        position: 2,
        name: "About BuildWithJIT",
        description: `Learn about ${profile.name} and the mission behind BuildWithJIT.`,
        url: "https://buildwithjit.com/about",
      },
      {
        "@type": "SiteNavigationElement",
        position: 3,
        name: "Author Profile",
        description: `Author profile of ${profile.name}, founder of ${profile.company}.`,
        url: "https://buildwithjit.com/author/jignesh-mevada",
      },
      {
        "@type": "SiteNavigationElement",
        position: 4,
        name: "Consultation & Contact",
        description: "Direct advisory, architecture reviews, and AI system design inquiries.",
        url: "https://buildwithjit.com/contact",
      },
    ],
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://buildwithjit.com/#organization",
    name: "BuildWithJIT",
    url: "https://buildwithjit.com",
    logo: {
      "@type": "ImageObject",
      url: "https://buildwithjit.com/icon-512.png",
      width: 512,
      height: 512,
    },
    sameAs: Object.values(social),
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://buildwithjit.com/#website",
    name: "BuildWithJIT",
    url: "https://buildwithjit.com",
    publisher: { "@id": "https://buildwithjit.com/#organization" },
  };

  return (
    <SiteShell social={social}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(navigationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      {/* 3D AI Neural Mind Hero Section */}
      <section className="hero">
        <div className="container-wide hero-inner">
          <div className="hero-copy">
            <div className="eyebrow">
              <span className="eyebrow-line" /> THE BUILDWITHJIT JOURNAL{" "}
              <span className="issue-number">/ ARCHITECTURE</span>
            </div>
            <h1>
              Production AI Automation, <em>RAG &amp; Voice AI Guides.</em>
            </h1>
            <p>
              Deep architectural guides, production benchmarks, and practical workflows for AI
              automation, RAG pipelines, voice AI agents, and enterprise engineering by{" "}
              {profile.name}.
            </p>
            <div className="hero-buttons">
              <Button asChild size="lg">
                <Link to="/" hash="latest">
                  Explore articles <ArrowRight size={18} />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">
                  Get in touch <ArrowUpRight size={18} />
                </Link>
              </Button>
            </div>
            <div className="hero-bottom">
              <span className="hero-symbol">✳</span>
              <span>AI AUTOMATION / RAG / VOICE AI</span>
            </div>
          </div>
          {showHero3D ? (
            <Suspense fallback={<div className="hero-canvas" />}>
              <HeroScene />
            </Suspense>
          ) : (
            <div className="hero-canvas" aria-hidden="true" />
          )}
          <div className="hero-index">
            AI / RAG / AUTOMATION
            <br />
            PRODUCTION SYSTEMS
          </div>
        </div>
      </section>

      {/* Intro Ticker */}
      <section className="intro-strip">
        <div className="container-wide strip-inner">
          <span>PRODUCTION RAG ARCHITECTURES.</span>
          <span className="strip-star">✳</span>
          <span>VOICE AI AGENTS &amp; LOW-LATENCY WEBSOCKETS.</span>
          <span className="strip-star">✳</span>
          <span>SELF-HEALING N8N ENTERPRISE AUTOMATION.</span>
        </div>
      </section>

      {/* Featured Story */}
      <section className="featured-section container-wide">
        <div className="section-heading">
          <div>
            <div className="section-kicker">01 / EDITOR'S PICK</div>
            <h2>
              Featured story<span className="period">.</span>
            </h2>
          </div>
          <span className="section-note">
            A good place to start <MoveUpRight size={16} />
          </span>
        </div>

        {articles.length > 0 && articles[0] ? (
          (() => {
            const featured = articles[0]!;
            return (
              <Link
                to="/articles/$slug"
                params={{ slug: featured.slug }}
                className="featured-layout"
              >
                <div className="featured-image">
                  <img
                    src={optimizeImageUrl(featured.image, 1100)}
                    alt={featured.title || "Featured article"}
                    width={1408}
                    height={912}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                  <span className="image-corner">
                    <ArrowUpRight size={21} />
                  </span>
                </div>
                <div className="featured-text">
                  <span className="pill">{featured.category}</span>
                  <h3>{featured.title}</h3>
                  <p>{featured.excerpt}</p>
                  <div className="story-meta">
                    <span>{formatDisplayDate(featured.date)}</span>
                    <span className="meta-dot" />
                    <span>{formatReadTime(featured.read)}</span>
                  </div>
                  <span className="read-link">
                    Read the story <ArrowRight size={18} />
                  </span>
                </div>
              </Link>
            );
          })()
        ) : (
          <div className="p-12 text-center text-muted-foreground border border-dashed rounded-xl">
            No featured story available. Please add some articles via the Admin portal.
          </div>
        )}
      </section>

      {/* Latest Articles */}
      <section className="latest-section" id="latest">
        <div className="container-wide">
          <div className="section-heading">
            <div>
              <div className="section-kicker">02 / THE ARCHIVE</div>
              <h2>
                Latest thinking<span className="period">.</span>
              </h2>
            </div>
            <span className="section-note">A collection of ideas worth your time.</span>
          </div>
          <div className="filter-row">
            <div className="filters">
              {categories.map((c) => (
                <Button
                  size="sm"
                  key={c}
                  variant={
                    category.trim().toLowerCase() === c.trim().toLowerCase() ? "default" : "ghost"
                  }
                  onClick={() => setCategory(c)}
                  className="cursor-pointer"
                >
                  {c}
                </Button>
              ))}
            </div>
            <label className="search-box">
              <Search size={17} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stories"
                aria-label="Search stories"
              />
            </label>
          </div>
          <div className="article-grid">
            {filtered.slice(0, 6).map((article, i) => (
              <Link
                to="/articles/$slug"
                params={{ slug: article.slug }}
                className="article-item"
                key={article.slug}
              >
                <div className="article-image">
                  <img
                    src={optimizeImageUrl(article.image, 640)}
                    alt={article.title}
                    width={1008}
                    height={704}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="image-corner">
                    <ArrowUpRight size={20} />
                  </span>
                </div>
                <div className="article-category">
                  <span>{article.category}</span>
                  <span>{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3>{article.title}</h3>
                <p>{article.excerpt}</p>
                <div className="story-meta">
                  <span>{formatDisplayDate(article.date)}</span>
                  <span className="meta-dot" />
                  <span>{formatReadTime(article.read)}</span>
                </div>
              </Link>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="empty-search">No stories found. Try another search.</p>
          )}

          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <Button asChild size="lg" variant="outline" className="explore-all-btn">
              <Link to="/articles">
                Explore all articles & archive <ArrowRight size={16} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Connect & Collaboration Band */}
      <section className="ai-band">
        <div className="container-wide ai-band-inner">
          <div className="ai-band-icon">
            <Mail size={30} />
          </div>
          <div className="ai-band-content">
            <div className="section-kicker">03 / ARCHITECTURE CONSULTING &amp; COLLABORATION</div>
            <h2>
              Building an AI system or automated workflow? <em>Let's connect.</em>
            </h2>
            <p>
              I build production RAG pipelines, voice AI agents, and enterprise n8n automations.
              Open for architectural consulting, system evaluations, and technical inquiries.
            </p>
          </div>
          <Button asChild size="lg" className="ai-band-cta">
            <Link to="/contact">
              Contact us <ArrowRight size={18} />
            </Link>
          </Button>
        </div>
      </section>
    </SiteShell>
  );
}
